import { laptopQueryKeys } from "@/api/query-keys/laptops"
import { Laptop } from "@/api/types/laptops"
import { TableHeaderActions } from "@/components/data-table/TableHeaderActions"
import { LoanLaptopToStudentForm } from "@/components/forms/laptops/LoanLaptopToStudentForm"
import { ReturnLaptopForm } from "@/components/forms/laptops/ReturnLaptopForm"
import { WriteOffLaptopForm } from "@/components/forms/laptops/WriteOffLaptopForm"
import ScreenWrapper from "@/components/ScreenWrapper"
import { laptopBrandMap } from "@/constants/laptops"
import { checkPermissions } from "@/utils/checkPermissions"
import { translateLaptopStatusToBadge } from "@/utils/translateLaptopStatusToBadge"
import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router"
import { ColumnDef } from "@tanstack/react-table"
import { useState } from "react"
import {
  createActionsColumn,
  createCustomColumn,
  createSortableColumn,
  createTableSearchSchema,
  DataTable,
  EditModal,
  getFullName,
  MultiLevelDropdownMenu,
  selectColumn,
  TABLE_PAGE_SIZE,
  useExportMutation,
  usePaginatedTableWithUrlState,
} from "toptaal-shared"
import {
  LAPTOP_STORAGE_KEY,
  useLaptopFilters,
} from "@/hooks/filters/useLaptopFilters"

const ALL_COLUMN_IDS = [
  "id",
  "serialNumber",
  "brand",
  "type",
  "currentStudent",
  "status",
]

export const Route = createFileRoute("/beheer/laptops/")({
  component: RouteComponent,
  beforeLoad: () => checkPermissions("read", "laptops"),
  validateSearch: createTableSearchSchema(),
})

function RouteComponent() {
  const [activeRow, setActiveRow] = useState<Laptop | null>(null)
  const [isOpen, setIsOpen] = useState(false)

  const [isWriteOffModalOpen, setIsWriteOffModalOpen] = useState(false)
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false)

  const router = useRouter()
  const searchParams = useSearch({ from: "/beheer/laptops/" })
  const navigate = useNavigate({ from: "/beheer/laptops/" })

  const { filterItems, activeFilters, queryFn } = useLaptopFilters({
    onPageReset: () =>
      navigate({ search: (prev) => ({ ...prev, page: 1 }), replace: true }),
  })

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    mergedFilters,
    selectedColumns,
    rowSelection,
    setRowSelection,
    selectedIds,
  } = usePaginatedTableWithUrlState({
    queryKey: [...laptopQueryKeys.all, activeFilters],
    queryFn,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: LAPTOP_STORAGE_KEY,
    allColumnIds: ALL_COLUMN_IDS,
  })

  const columns: ColumnDef<Laptop>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createSortableColumn({ accessorKey: "serialNumber", name: "Serie nr." }),
    createCustomColumn({
      id: "brand",
      accessorKey: "brand",
      name: "Merk",
      cell: ({ row }) => laptopBrandMap[row.original.brand],
    }),
    createSortableColumn({ accessorKey: "type", name: "Type" }),
    createCustomColumn({
      id: "currentStudent",
      accessorKey: "currentStudent",
      name: "Uitgeleend aan",
      cell: ({ row }) => {
        const student = row.original.currentStudent

        return student ? getFullName(student) : "—"
      },
    }),
    createCustomColumn({
      id: "status",
      accessorKey: "status",
      name: "Status",
      cell: ({ row }) => translateLaptopStatusToBadge(row.original.status),
    }),
    createActionsColumn([
      {
        label: "Uitlenen aan cursist",
        onClick: (e, laptop) => {
          e.stopPropagation()
          setActiveRow(laptop)
          setIsOpen(true)
        },
        hidden: (laptop) =>
          laptop.status === "in_use" || laptop.status === "written_off",
      },
      {
        label: "Afschrijven",
        onClick: (e, laptop) => {
          e.stopPropagation()
          setActiveRow(laptop)
          setIsWriteOffModalOpen(true)
        },
        hidden: (laptop) =>
          laptop.status === "in_use" || laptop.status === "written_off",
      },
      {
        label: "Inleveren",
        onClick: (e, laptop) => {
          e.stopPropagation()
          setActiveRow(laptop)
          setIsReturnModalOpen(true)
        },
        hidden: (laptop) => laptop.status !== "in_use",
      },
    ]),
  ]

  const { mutate: exportLaptops } = useExportMutation({
    exportApiPrefix: "laptops",
    filters: mergedFilters,
    selectedColumns,
    selectedIds,
    activeFilters,
  })

  const handleNavigateDetail = (laptopId: Laptop["id"]) => {
    navigate({
      to: "/beheer/laptops/$laptopId/leengeschiedenis",
      params: { laptopId: laptopId.toString() },
      search: { from: router.state.location.href },
    })
  }

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title="Laptops"
        data={data?.data.laptops || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        rowSelection={rowSelection}
        setRowSelection={setRowSelection}
        columns={columns}
        onRowClick={handleNavigateDetail}
        headerLeftActions={<MultiLevelDropdownMenu items={filterItems} />}
        headerRightActions={
          <TableHeaderActions
            createAction={{
              label: "Nieuwe laptop",
              to: "/beheer/laptops/aanmaken",
              entity: "laptops",
              permissions: "create",
            }}
            exportAction={{ onClick: exportLaptops }}
          />
        }
      />

      {isOpen && activeRow && (
        <EditModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          title={`${laptopBrandMap[activeRow.brand]} ${activeRow.serialNumber} uitlenen aan cursist`}
        >
          <LoanLaptopToStudentForm
            setIsOpen={setIsOpen}
            laptopId={activeRow.id}
          />
        </EditModal>
      )}

      {isWriteOffModalOpen && activeRow && (
        <EditModal
          isOpen={isWriteOffModalOpen}
          setIsOpen={setIsWriteOffModalOpen}
          title={`${laptopBrandMap[activeRow.brand]} ${activeRow.serialNumber} afschrijven`}
        >
          <WriteOffLaptopForm
            laptopId={activeRow.id}
            setIsOpen={setIsWriteOffModalOpen}
            invalidateTargets={["list"]}
          />
        </EditModal>
      )}

      {isReturnModalOpen && activeRow && (
        <EditModal
          isOpen={isReturnModalOpen}
          setIsOpen={setIsReturnModalOpen}
          title={`${laptopBrandMap[activeRow.brand]} ${activeRow.serialNumber} inleveren`}
        >
          <ReturnLaptopForm
            laptopId={activeRow.id}
            setIsOpen={setIsReturnModalOpen}
            invalidateTargets={["list"]}
          />
        </EditModal>
      )}
    </ScreenWrapper>
  )
}
